import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Link, useParams } from "react-router-dom";
import { EeljProps } from "./EeljInfo";
import { UserContext } from "../../providers/UserProvider";
import FloatingShapes from "../common/FloatingShapes";

export const FAQ = () => {
  const { t } = useTranslation();
  const faqs = [
    {
      question: t('eeljDetail.faq1q'),
      answer: t('eeljDetail.faq1a'),
    },
    {
      question: t('eeljDetail.faq2q'),
      answer: t('eeljDetail.faq2a'),
    },
    {
      question: t('eeljDetail.faq3q'),
      answer: t('eeljDetail.faq3a'),
    },
    {
      question: t('eeljDetail.faq4q'),
      answer: t('eeljDetail.faq4a'),
    }
  ];

  return (
    <div className="relative overflow-hidden bg-primary-800">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-primary-600/20 blur-[120px]" />
        <div className="absolute bottom-0 -left-40 w-[400px] h-[400px] rounded-full bg-secondary-500/10 blur-[100px]" />
      </div>
      <FloatingShapes variant="alt" />
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`, backgroundSize: '60px 60px' }} />
      <div className="relative mx-auto max-w-[1120px] px-6 lg:px-10 py-16">
        <h2 className="text-[22px] font-extrabold mb-8">
          <span className="bg-gradient-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent">{t('eeljDetail.faqTitle')}</span>
        </h2>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} className="bg-white/5 border border-white/10 backdrop-blur-sm rounded-xl p-6">
              <dt className="text-base font-semibold text-white mb-2">
                {faq.question}
              </dt>
              <dd className="text-base text-slate-400 leading-relaxed">
                {faq.answer}
              </dd>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
};

export const EeljFull = (props: EeljProps) => {
  const { t } = useTranslation();
  const {userBio} = useContext(UserContext);
  const {id, name, active, title, description, thumb, price, ITtrainingPrice, start_date, end_date, ageGroup, orderStartDate, orderEndDate} = props;
  const [curDate, setCurDate] = useState(new Date());

  const getDateString = (date: string | Date) => {
    return new Date(date).toISOString().split('T')[0].replaceAll('-', '.');
  }

  const countdownString = useMemo(() => {
    const diff = new Date(orderStartDate).valueOf() - curDate.valueOf();

    const formatDuration = (millis: number) => {
      let date = new Date(millis);
      let parts = [];

      if (date.getUTCFullYear() > 1970) {
          let years = date.getUTCFullYear() - 1970;
          parts.push(years, 'жил');
      }
      if (date.getUTCMonth() > 0) { // months start at zero
          let months = date.getUTCMonth();
          parts.push(months, 'сар');
      }
      if (date.getUTCDate() > 1) {
          let days = date.getUTCDate() - 1;
          parts.push(days, 'өдөр');
      }

      parts.push(date.toISOString().substring(11, 19));
      return parts.join(' ');
    }

    return formatDuration(diff);
  }, [curDate, orderStartDate]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurDate(new Date());
    }, 1000);

    return () => {
      clearInterval(interval);
    }
  }, []);


  const orderStatus = new Date(orderEndDate) > curDate
    ? curDate > new Date(orderStartDate)
      ? 'active'
      : 'pending'
    : 'ended';

  return (
    <>
      {/* Dark hero with title */}
      <div className="relative overflow-hidden bg-primary-800 -mt-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-primary-600/20 blur-[120px]" />
          <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] rounded-full bg-secondary-500/10 blur-[100px]" />
        </div>
        <FloatingShapes variant="default" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`, backgroundSize: '60px 60px' }} />
        <div className="relative pt-40 pb-12 sm:pt-48 sm:pb-16">
          <div className="mx-auto max-w-[1120px] px-6 lg:px-10">
            <h1 className="text-[26px] sm:text-[32px] font-extrabold text-white leading-tight">
              {title}
            </h1>
            <div className="flex flex-wrap items-center gap-2 mt-3 text-sm">
              <span className="font-semibold text-white">{name}</span>
              <span className="text-slate-500">·</span>
              <span className="text-slate-400">{ageGroup}</span>
              <span className="text-slate-500">·</span>
              <span className="text-slate-400">{getDateString(start_date)} – {getDateString(end_date)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white pb-12">
        <div className="mx-auto max-w-[1120px] px-6 lg:px-10">
          {/* Hero image with glow */}
          <div className="relative -mt-6">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary-500/20 via-transparent to-primary-500/20 rounded-2xl blur-2xl" />
            <div className="relative rounded-xl overflow-hidden border border-neutral-200 shadow-xl">
              <img src={thumb} alt={title} className="w-full h-[300px] sm:h-[400px] lg:h-[480px] object-cover" />
            </div>
          </div>

          {/* Content Grid */}
          <div className="grid lg:grid-cols-[1fr_380px] gap-12 lg:gap-16 items-start mt-10">
            {/* Left - Description */}
            <div>
              {/* Host-style info bar */}
              <div className="flex items-center gap-4 pb-6 border-b border-neutral-200">
                <div className="w-14 h-14 rounded-full bg-primary-600 flex items-center justify-center text-white text-xl font-bold">
                  N
                </div>
                <div>
                  <p className="font-semibold text-neutral-900">{t('eeljDetail.nairamdal')}</p>
                  <p className="text-sm text-neutral-500">{t('eeljDetail.intlCamp')}</p>
                </div>
              </div>

              {/* Features */}
              <div className="py-6 border-b border-neutral-200 space-y-6">
                <div className="flex gap-4">
                  <svg className="w-6 h-6 text-neutral-700 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                  </svg>
                  <div>
                    <p className="font-semibold text-neutral-900 text-[15px]">{t('eeljDetail.duration')}</p>
                    <p className="text-sm text-neutral-500">{getDateString(start_date)} – {getDateString(end_date)}</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <svg className="w-6 h-6 text-neutral-700 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                  </svg>
                  <div>
                    <p className="font-semibold text-neutral-900 text-[15px]">{t('eeljDetail.ageGroup')}</p>
                    <p className="text-sm text-neutral-500">{ageGroup}</p>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="py-6 border-b border-neutral-200">
                <p className="text-base text-neutral-700 leading-relaxed">
                  {description}
                </p>
              </div>
            </div>

            {/* Right - Booking Card */}
            <div className="lg:sticky lg:top-24">
              <div className="border border-neutral-200 rounded-xl p-6 shadow-lg">
                {/* Price */}
                <div className="mb-6">
                  <span className="text-[22px] font-extrabold text-neutral-900">
                    ₮{(price + ITtrainingPrice).toLocaleString()}
                  </span>
                  <span className="text-base text-neutral-500"> {t('eelj.perChild')}</span>
                </div>

                {/* Info rows */}
                <div className="border border-neutral-200 rounded-xl overflow-hidden mb-4">
                  <div className="grid grid-cols-2 divide-x divide-neutral-200">
                    <div className="p-3">
                      <p className="text-[10px] font-bold text-neutral-900 uppercase tracking-wider">{t('eeljDetail.startDate')}</p>
                      <p className="text-sm text-neutral-700 mt-0.5">{getDateString(start_date)}</p>
                    </div>
                    <div className="p-3">
                      <p className="text-[10px] font-bold text-neutral-900 uppercase tracking-wider">{t('eeljDetail.endDate')}</p>
                      <p className="text-sm text-neutral-700 mt-0.5">{getDateString(end_date)}</p>
                    </div>
                  </div>
                  <div className="border-t border-neutral-200 p-3">
                    <p className="text-[10px] font-bold text-neutral-900 uppercase tracking-wider">{t('eeljDetail.ageGroup')}</p>
                    <p className="text-sm text-neutral-700 mt-0.5">{ageGroup}</p>
                  </div>
                </div>

                {/* CTA Button */}
                <div>
                  {orderStatus === 'active' ? (
                    userBio ? (
                      <Link
                        to={`/payment/${id}`}
                        className="block w-full text-center py-3.5 rounded-lg bg-gradient-to-r from-primary-600 via-secondary-500 to-primary-400 text-white font-semibold text-base hover:opacity-90 transition-opacity"
                      >
                        {t('eeljDetail.purchase')}
                      </Link>
                    ) : (
                      <Link
                        to={`/login`}
                        className="block w-full text-center py-3.5 rounded-lg bg-gradient-to-r from-primary-600 via-secondary-500 to-primary-400 text-white font-semibold text-base hover:opacity-90 transition-opacity"
                      >
                        {t('eeljDetail.loginToPurchase')}
                      </Link>
                    )
                  ) : orderStatus === 'pending' ? (
                    <div className="w-full text-center py-3.5 rounded-lg bg-neutral-900 text-white font-semibold text-base">
                      <p className="text-sm">{t('eeljDetail.orderStartsIn')}</p>
                      <p className="text-xs font-normal text-neutral-300 mt-1">{countdownString} {t('eeljDetail.remaining')}</p>
                    </div>
                  ) : (
                    <div className="w-full text-center py-3.5 rounded-lg bg-neutral-100 text-neutral-400 font-semibold text-base cursor-not-allowed">
                      {t('eeljDetail.orderEnded')}
                    </div>
                  )}
                </div>

                {/* Subtle note */}
                <p className="text-center text-xs text-neutral-400 mt-3">
                  {t('eeljDetail.noChargeYet')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export const EeljDetail = () => {
  const {id} = useParams();
  const {eeljuud} = useContext(UserContext);

  const eelj = useMemo(() => {
    return [...eeljuud.activeEelj, ...eeljuud.eelj].find(e => e.id === id);
  }, [id, eeljuud]);

  return (
    <>
      {eelj &&
        <>
          <EeljFull {...eelj}/>
          <FAQ/>
        </>
      }
    </>
  )
}

export default EeljDetail;
